import CoachOrderCard, { CoachOrderFact } from './CoachOrderCard';

/**
 * 보내기 전 미리보기 — "강사 화면에서 대략 이렇게 보입니다".
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 확인 창 안에 있나
 * ─────────────────────────────────────────────────────────────
 * 확인 창은 원장님이 **마지막으로 눈을 두는 자리**입니다. 거기서 보이는 것은
 * 지금까지 "무슨 값이 나가는가"(강사·회원·문장)뿐이었는데, 원장님이 정작
 * 궁금해하는 것은 "강사가 이걸 열면 뭘 보는가"입니다. 값의 목록과 실제 화면은
 * 다르게 읽힙니다 — 특히 문구 자리가 비어 있을 때 그렇습니다.
 *
 * 그래서 옆에 폰 목업을 세우고, 확인 창에서 글을 고치면 **그 자리에서 같이**
 * 바뀌게 했습니다. 「강사에게 덧붙일 말」을 적으면 「관리자님 메모」가 생깁니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 여기 있는 것은 전부 실제와 같아야 합니다
 * ─────────────────────────────────────────────────────────────
 *  · 카드 모양·순서·이름표 → CoachOrderCard (rappoApp ModalCoachOpsOrder 를 따름)
 *  · 푸시 배너 문구        → 서버 템플릿 그대로 (아래 상수)
 *  · 문구 자리            → 경로에 따라 다르다 (아래 DRAFT_BY_APP_NOTICE)
 *
 * 버튼은 **장식입니다.** 누를 수 없고 아무것도 저장하지 않습니다.
 * 그 사실을 화면에 적어 둡니다.
 */

const PHONE_W = 302;

/**
 * 푸시 알림 문구 — **서버 템플릿과 같은 조합이어야 한다.**
 *
 * rappo `src/constants/pushTemplate.js` COA_ORDER:
 *   TITLE: () => `관리자님이 보낸 오늘의 할 일`      ← 고정. 회원 이름이 안 들어간다
 *   BODY : (MEM_NAME, TASK) => `${MEM_NAME} 회원 · ${TASK}`
 *
 * 여기서 그럴듯한 문장을 지어내면, 원장님이 본 배너와 강사 잠금화면이 달라집니다.
 * 서버가 템플릿을 바꾸면 여기도 같이 바꿔야 합니다.
 */
export const COACH_PUSH_TITLE = '관리자님이 보낸 오늘의 할 일';
export const coachPushBody = (memberName: string, task: string) => `${memberName} 회원 · ${task}`;

/**
 * 문구를 안 보내는 경로에서 그 **자리에** 적을 말.
 *
 * 회원 현황에서 낸 지시는 `memberDraft` 를 아예 싣지 않습니다(2026-08 대표 결정).
 * 그러면 서버는 null 로 저장하고, 강사 앱이 회원 이름과 근거로 첫 마디를 만듭니다
 * (rappoApp features/opsOrder/draft.ts). 그 문장을 웹이 흉내 내면 원장이 본 문장과
 * 강사가 받는 문장이 갈리므로, **만들어진다는 사실만** 적습니다.
 */
export const DRAFT_BY_APP_NOTICE =
    '문구는 강사 앱이 회원 이름과 근거로 첫 마디를 만들어 보여줍니다';

type Props = {
    memberName: string;
    coachName: string;
    task: string;
    /** 확인 창에서 지금 입력 중인 값. 그대로 「관리자님 메모」가 된다 */
    ownerNote: string;
    /**
     * 회원에게 건넬 문구. **부르는 쪽이 서버 저장값과 같은 값으로 넘긴다.**
     * 없으면(null) 문구 자리에 DRAFT_BY_APP_NOTICE 가 대신 들어간다.
     */
    memberDraft: string | null;
    reason?: string | null;
    facts?: CoachOrderFact[];
};

/** 장식용 버튼 — 진짜 버튼으로 만들지 않는다. 여기서 누를 것은 없다 */
const FakeButton = ({
    label,
    primary = false,
    flex = 1,
}: {
    label: string;
    primary?: boolean;
    flex?: number;
}) => (
    <div
        className="flex items-center justify-center rounded-[10px]"
        style={{
            flex,
            height: 38,
            background: primary ? '#1A305D' : '#FFFFFF',
            border: primary ? 'none' : '1px solid #DCE4EE',
            color: primary ? '#FFFFFF' : '#425063',
            fontSize: 13.5,
            fontWeight: 700,
        }}
    >
        {label}
    </div>
);

const CoachOrderPreview = ({
    memberName,
    coachName,
    task,
    ownerNote,
    memberDraft,
    reason,
    facts = [],
}: Props) => (
    <div
        className="flex flex-col items-center"
        role="group"
        aria-label="강사 앱 미리보기"
        style={{ gap: 10 }}
    >
        <div className="flex flex-col items-center" style={{ gap: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF' }}>
                {coachName} 강사 화면에서는 이렇게 보입니다
            </span>
            <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.6)' }}>
                미리보기입니다 · 여기서는 아무것도 누를 수 없습니다
            </span>
        </div>

        {/*
          잠금화면 배너.
          강사가 가장 먼저 보는 것은 카드가 아니라 이 두 줄이다.
        */}
        <div style={{ width: PHONE_W }}>
            <div
                className="rounded-[14px]"
                style={{ background: 'rgba(255,255,255,0.94)', padding: '10px 12px' }}
            >
                <div className="flex items-center" style={{ gap: 6 }}>
                    <div
                        className="rounded-[5px] flex items-center justify-center"
                        style={{ width: 16, height: 16, background: '#1A305D' }}
                    >
                        <span style={{ fontSize: 9, fontWeight: 800, color: '#FFFFFF' }}>R</span>
                    </div>
                    <span style={{ fontSize: 10.5, color: '#7C8794', letterSpacing: '0.02em' }}>
                        라포
                    </span>
                    <span style={{ fontSize: 10.5, color: '#B4BDC9', marginLeft: 'auto' }}>지금</span>
                </div>
                <div style={{ marginTop: 5 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: '#16202E' }}>
                        {COACH_PUSH_TITLE}
                    </span>
                </div>
                <div style={{ marginTop: 2 }}>
                    <span style={{ fontSize: 12.5, color: '#425063', lineHeight: '18px' }}>
                        {coachPushBody(memberName, task)}
                    </span>
                </div>
            </div>
        </div>

        {/* 폰 프레임 — 앱은 아래에서 올라오는 시트로 띄운다 */}
        <div
            className="flex flex-col overflow-hidden"
            style={{
                width: PHONE_W,
                background: '#FFFFFF',
                borderRadius: '22px 22px 26px 26px',
                border: '7px solid #10182A',
                boxShadow: '0 18px 40px rgba(0,0,0,0.35)',
            }}
        >
            <div className="flex flex-col items-center" style={{ padding: '9px 0 0' }}>
                <div style={{ width: 34, height: 4, borderRadius: 2, background: '#DCE4EE' }} />
            </div>
            <div style={{ padding: '11px 14px 10px', borderBottom: '1px solid #EDF1F6' }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#16202E' }}>
                    {COACH_PUSH_TITLE}
                </span>
            </div>

            <div style={{ padding: 12, background: '#F4F6F9' }}>
                <CoachOrderCard
                    memberName={memberName}
                    reason={reason}
                    facts={facts}
                    task={task}
                    ownerNote={ownerNote}
                    memberDraft={memberDraft}
                    draftFallback={
                        <span style={{ fontSize: 12, color: '#7C8794', lineHeight: '18px' }}>
                            {DRAFT_BY_APP_NOTICE}
                        </span>
                    }
                    footer={
                        <div className="flex flex-col" style={{ marginTop: 13, gap: 7 }}>
                            <div className="flex" style={{ gap: 7 }}>
                                <FakeButton label="채팅으로 연락" flex={1.9} />
                                <FakeButton label="전화 걸기" />
                            </div>
                            <FakeButton label="완료했어요" primary />
                            <div className="flex justify-center" style={{ paddingTop: 2 }}>
                                <span style={{ fontSize: 12.5, color: '#7C8794' }}>나중에</span>
                            </div>
                        </div>
                    }
                />
            </div>
        </div>
    </div>
);

export default CoachOrderPreview;
